import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNotification } from '../../../../../context/NotificationManager/NotificationManager'
import { getTarifasAlumno } from '../../../../../utils/api/tarifas'
import {
  bulkDelete,
  bulkUpdateAsignatura,
  bulkUpdateCentro,
  bulkUpdateProfesor,
  getAsistenciasContratacion,
  planificarAsistencias,
  updateAsistencia
} from '../../../../../utils/api/asistencias'
import { getPacksAlumno } from '../../../../../utils/api/packs'
import {
  dateToString,
  forwardMonths,
  getDateFromObjectId,
  getToday
} from '../../../../../utils/date'
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import PageLoading from '../../../../../components/PageLoading/PageLoading'
import { isAnyBlank, pluralize } from '../../../../../utils/string'
import InformationTable from '../../../../../components/Table/InformationTable'
import { dateSort } from '../../../../../utils/table'
import EstadoAsistencia from '../../../../../components/EstadoAsistencia/EstadoAsistencia'
import ModalPlanificarClasesTarifa from '../../../../../modals/ModalPlanificarClasesTarifa/ModalPlanificarClasesTarifa'
import ModalPlanificarClasesPack from '../../../../../modals/ModalPlanificarClasesPack/ModalPlanificarClasesPack'
import ModalEditarAsistencia from '../../../../../modals/ModalEditarAsistencia/ModalEditarAsistencia'
import ModalEditarProfesorAsistencias from '../../../../../modals/ModalEditarProfesorAsistencias/ModalEditarProfesorAsistencias'
import ModalEditarCentroAsistencias from '../../../../../modals/ModalEditarCentroAsistencias/ModalEditarCentroAsistencias'
import ModalEditarAsignaturaAsistencias from '../../../../../modals/ModalEditarAsignaturaAsistencias/ModalEditarAsignaturaAsistencias'
import css from './GestionarAsistencias.module.css'

const SinProfesor = () => <span className={css.placeholder}>Sin profesor</span>

const getDatosContratacion = ({ contratacionSeleccionada, tarifas, packs }) => {
  const tarifa = tarifas.find(tarifa => tarifa.id === contratacionSeleccionada)
  if (tarifa) return { ...tarifa, contratacion: 'tarifa' }
  const pack = packs.find(pack => pack.id === contratacionSeleccionada)
  if (pack) return { ...pack, contratacion: 'pack' }
  return {}
}

const ContratacionSelector = ({ tarifas, packs, value, onChange }) => {
  const tarifasRecientes = tarifas.filter(tarifa => {
    const fechaActivacion = getDateFromObjectId(tarifa.id)
    const fechaReferencia = forwardMonths(getToday(), -14)
    return fechaActivacion >= fechaReferencia
  })

  const packsRecientes = packs.filter(pack => {
    const fechaActivacion = getDateFromObjectId(pack.id)
    const fechaReferencia = forwardMonths(getToday(), -14)
    return fechaActivacion >= fechaReferencia
  })

  return (
    <FormControl
      className={css.contratacionSelector}
      variant='standard'
      margin='dense'
      fullWidth
    >
      <InputLabel id='contratacion'>Elegir tarifa o pack</InputLabel>
      <Select
        name='contratacion'
        labelId='contratacion'
        id='contratacion'
        value={value}
        onChange={onChange}
        label='Elegir tarifa o pack'
      >
        {tarifasRecientes.length > 0 && <ListSubheader>Tarifas</ListSubheader>}
        {tarifasRecientes.map(contratacion => (
          <MenuItem key={contratacion.id} value={contratacion.id}>
            {contratacion.tarifa} ({contratacion.mes})
          </MenuItem>
        ))}
        {packsRecientes.length > 0 && (
          <ListSubheader>Packs de horas</ListSubheader>
        )}
        {packsRecientes.map(contratacion => (
          <MenuItem key={contratacion.id} value={contratacion.id}>
            {contratacion.pack} - {contratacion.horasConsumidas}/
            {contratacion.horas} horas ({contratacion.fecha})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const GestionarAsistencias = ({ alumnoId }) => {
  const notification = useNotification()
  const queryClient = useQueryClient()

  const [contratacionSeleccionada, setContratacionSeleccionada] = useState('')
  const [asistenciasSeleccionadas, setAsistenciasSeleccionadas] = useState([])

  const [asistenciaEdit, setAsistenciaEdit] = useState(null)

  const [openPlanificarTarifa, setOpenPlanificarTarifa] = useState(false)
  const [openPlanificarPack, setOpenPlanificarPack] = useState(false)

  const [bulkEditProfesor, setBulkEditProfesor] = useState(false)
  const [bulkEditCentro, setBulkEditCentro] = useState(false)
  const [bulkEditAsignatura, setBulkEditAsignatura] = useState(false)

  const { data: alumnoTarifas = [], isLoading: cargandoTarifas } = useQuery({
    queryKey: [
      'alumnos',
      'contabilidad',
      'tarifa-alumno',
      'list',
      'alumno',
      alumnoId
    ],
    queryFn: () =>
      getTarifasAlumno(alumnoId)
        .then(datos => datos)
        .catch(err => {
          notification.error({
            title: 'Error obteniendo tarifas',
            content: err
          })
          return []
        })
  })

  const { data: alumnoPacks = [], isLoading: cargandoPacks } = useQuery({
    queryKey: [
      'alumnos',
      'contabilidad',
      'pack-horas-alumno',
      'list',
      'alumno',
      alumnoId
    ],
    queryFn: () =>
      getPacksAlumno(alumnoId)
        .then(datos =>
          datos.map(pack => ({
            ...pack,
            fecha: dateToString(getDateFromObjectId(pack.id))
          }))
        )
        .catch(err => {
          notification.error({ title: 'Error obteniendo packs', content: err })
          return []
        })
  })

  const contratacion = getDatosContratacion({
    contratacionSeleccionada,
    tarifas: alumnoTarifas,
    packs: alumnoPacks
  })

  const { data: asistencias = [], isLoading: cargandoAsistencias } = useQuery({
    queryKey: [
      'alumnos',
      'contabilidad',
      'asistencia',
      'list',
      contratacion.contratacion,
      contratacion.id
    ],
    queryFn: () =>
      getAsistenciasContratacion(contratacion.contratacion, contratacion.id)
        .then(datos =>
          datos
            .sort((a, b) => {
              if (a.fecha < b.fecha) return -1
              if (a.fecha > b.fecha) return 1
              if (a.hora < b.hora) return -1
              if (a.hora > b.hora) return 1
              return 0
            })
            .map(asistencia => ({
              ...asistencia,
              fecha: dateToString(asistencia.fecha)
            }))
        )
        .catch(err => {
          notification.error({
            title: 'Error obteniendo asistencias',
            content: err
          })
          return []
        }),
    enabled: !isAnyBlank(contratacion.id, contratacion.contratacion)
  })

  const { isPending: isAsistenciaUpdating, mutate: actualizarAsistencia } =
    useMutation({
      mutationFn: updateAsistencia,
      onSuccess: () => {
        notification.success({
          title: 'Asistencia actualizada',
          content: 'Se ha modificado correctamente'
        })
        queryClient.invalidateQueries({
          queryKey: [
            'alumnos',
            'contabilidad',
            'asistencia',
            'list',
            contratacion.contratacion,
            contratacion.id
          ]
        })
        setAsistenciaEdit(null)
      },
      onError: err => {
        notification.error({
          title: 'Error actualizando la asistencia',
          content: err
        })
      }
    })

  const { isPending: isAsistenciaPlanning, mutate: planificacionAsistencias } =
    useMutation({
      mutationFn: planificarAsistencias,
      onSuccess: () => {
        notification.success({
          title: 'Asistencias programadas',
          content: 'Se han programado correctamente'
        })
        queryClient.invalidateQueries({
          queryKey: [
            'alumnos',
            'contabilidad',
            'asistencia',
            'list',
            contratacion.contratacion,
            contratacion.id
          ]
        })
        setOpenPlanificarTarifa(false)
        setOpenPlanificarPack(false)
      },
      onError: err => {
        notification.error({
          title: 'Error programando las asistencias',
          content: err
        })
      }
    })

  const { isPending: isBulkUpdatingProfesor, mutate: multipleUpdateProfesor } =
    useMutation({
      mutationFn: bulkUpdateProfesor,
      onSuccess: () => {
        notification.success({
          title: 'Asistencias actualizadas',
          content: 'Profesor asignado correctamente'
        })
        queryClient.invalidateQueries({
          queryKey: [
            'alumnos',
            'contabilidad',
            'asistencia',
            'list',
            contratacion.contratacion,
            contratacion.id
          ]
        })
        setBulkEditProfesor(false)
      },
      onError: err => {
        notification.error({ title: 'Error asignando profesor', content: err })
      }
    })

  const { isPending: isBulkUpdatingCentro, mutate: multipleUpdateCentro } =
    useMutation({
      mutationFn: bulkUpdateCentro,
      onSuccess: () => {
        notification.success({
          title: 'Asistencias actualizadas',
          content: 'Academia asignada correctamente'
        })
        queryClient.invalidateQueries({
          queryKey: [
            'alumnos',
            'contabilidad',
            'asistencia',
            'list',
            contratacion.contratacion,
            contratacion.id
          ]
        })
        setBulkEditCentro(false)
      },
      onError: err => {
        notification.error({ title: 'Error asignando academia', content: err })
      }
    })

  const {
    isPending: isBulkUpdatingAsignatura,
    mutate: multipleUpdateAsignatura
  } = useMutation({
    mutationFn: bulkUpdateAsignatura,
    onSuccess: () => {
      notification.success({
        title: 'Asistencias actualizadas',
        content: 'Asignatura asignada correctamente'
      })
      queryClient.invalidateQueries({
        queryKey: [
          'alumnos',
          'contabilidad',
          'asistencia',
          'list',
          contratacion.contratacion,
          contratacion.id
        ]
      })
      setBulkEditAsignatura(false)
    },
    onError: err => {
      notification.error({ title: 'Error asignando asignatura', content: err })
    }
  })

  const { isPending: isDeletingAsistencias, mutate: deleteAsistencias } =
    useMutation({
      mutationFn: bulkDelete,
      onSuccess: () => {
        notification.success({
          title: 'Asistencias borradas',
          content: 'Se han borrado correctamente'
        })
        queryClient.invalidateQueries({
          queryKey: [
            'alumnos',
            'contabilidad',
            'asistencia',
            'list',
            contratacion.contratacion,
            contratacion.id
          ]
        })
        setAsistenciasSeleccionadas([])
      },
      onError: err => {
        notification.error({
          title: 'Error borrando las asistencias',
          content: err
        })
      }
    })

  const asignaturas = asistencias.reduce((acc, asistencia) => {
    const asignatura = `${asistencia.asignatura} (${asistencia.nivel})`
    if (!acc.includes(asignatura)) acc.push(asignatura)
    return acc
  }, [])

  const handleAsistenciaSeleccionada = asistenciaId => {
    if (asistenciasSeleccionadas.includes(asistenciaId)) {
      setAsistenciasSeleccionadas(
        asistenciasSeleccionadas.filter(id => id !== asistenciaId)
      )
    } else {
      setAsistenciasSeleccionadas([...asistenciasSeleccionadas, asistenciaId])
    }
  }

  const handleAllAsistenciasSeleccionadas = () => {
    if (asistenciasSeleccionadas.length === asistencias.length) {
      setAsistenciasSeleccionadas([])
    } else {
      setAsistenciasSeleccionadas(asistencias.map(asistencia => asistencia.id))
    }
  }

  const handleOpenPlanificarContratacion = () => {
    if (contratacion.contratacion === 'tarifa') {
      setOpenPlanificarTarifa(true)
    }
    if (contratacion.contratacion === 'pack') {
      setOpenPlanificarPack(true)
    }
  }

  const handlePlanificarClases = ({
    asignatura,
    profesor,
    centro,
    tipo,
    observaciones,
    horas
  }) => {
    if (isAsistenciaPlanning) return
    planificacionAsistencias({
      alumno: alumnoId,
      profesor,
      clase: tipo,
      centro,
      tipoContratacion: contratacion.contratacion,
      contratacion: contratacion.id,
      asignatura,
      asistencias: horas,
      observaciones
    })
  }

  const handleUpdateAsistencia = ({
    id,
    asignatura,
    profesor,
    centro,
    fecha,
    hora,
    observaciones
  }) => {
    if (isAsistenciaUpdating) return
    actualizarAsistencia({
      id,
      asignatura,
      profesor,
      centro,
      fecha,
      hora,
      observaciones
    })
  }

  const handleUpdateAcademia = ({ centro }) => {
    if (isBulkUpdatingCentro) return
    multipleUpdateCentro({ asistencias: asistenciasSeleccionadas, centro })
  }

  const handleUpdateProfesor = ({ profesor }) => {
    if (isBulkUpdatingProfesor) return
    multipleUpdateProfesor({ asistencias: asistenciasSeleccionadas, profesor })
  }

  const handleUpdateAsignatura = ({ asignatura }) => {
    if (isBulkUpdatingAsignatura) return
    multipleUpdateAsignatura({
      asistencias: asistenciasSeleccionadas,
      asignatura
    })
  }

  const handleModificarAcademia = () => setBulkEditCentro(true)
  const handleModificarProfesor = () => setBulkEditProfesor(true)
  const handleModificarAsignatura = () => setBulkEditAsignatura(true)

  const handleBorrarAsistencias = () => {
    if (isDeletingAsistencias) return
    deleteAsistencias({ asistencias: asistenciasSeleccionadas })
  }

  return (
    <PageLoading isLoading={cargandoTarifas || cargandoPacks}>
      <div className={css.main}>
        <div className={css.contratacion}>
          <ContratacionSelector
            tarifas={alumnoTarifas}
            packs={alumnoPacks}
            value={contratacionSeleccionada}
            onChange={e => setContratacionSeleccionada(e.target.value)}
          />
        </div>
        {asistenciasSeleccionadas.length > 0 && (
          <div className={css.bulk}>
            <Chip
              className={css.bulkAction}
              icon={<i className='material-icons'>account_balance</i>}
              label={`Modificar academia de ${asistenciasSeleccionadas.length} ${pluralize('asistencia', asistenciasSeleccionadas.length)}`}
              color={isBulkUpdatingCentro ? 'default' : 'primary'}
              variant='outlined'
              onClick={handleModificarAcademia}
            />
            <Chip
              className={css.bulkAction}
              icon={<i className='material-icons'>school</i>}
              label={`Modificar profesor de ${asistenciasSeleccionadas.length} ${pluralize('asistencia', asistenciasSeleccionadas.length)}`}
              color={isBulkUpdatingProfesor ? 'default' : 'secondary'}
              variant='outlined'
              onClick={handleModificarProfesor}
            />
            <Chip
              className={css.bulkAction}
              icon={<i className='material-icons'>architecture</i>}
              label={`Modificar asignatura de ${asistenciasSeleccionadas.length} ${pluralize('asistencia', asistenciasSeleccionadas.length)}`}
              color={isBulkUpdatingAsignatura ? 'default' : 'tertiary'}
              variant='outlined'
              onClick={handleModificarAsignatura}
            />
            <Chip
              className={css.bulkAction}
              icon={<i className='material-icons'>delete</i>}
              label={`Borrar ${asistenciasSeleccionadas.length} ${pluralize('asistencia', asistenciasSeleccionadas.length)}`}
              color={isDeletingAsistencias ? 'default' : 'error'}
              variant='outlined'
              onClick={handleBorrarAsistencias}
            />
          </div>
        )}
        {contratacionSeleccionada && !cargandoAsistencias && (
          <div className={css.title}>
            <Typography variant='body 2'>
              {asistencias.length} {pluralize('asistencia', asistencias.length)}{' '}
              de {asignaturas.length}{' '}
              {pluralize('asignatura', asignaturas.length)}
            </Typography>
            <Button
              variant='outlined'
              color='primary'
              size='small'
              startIcon={<i className='material-icons'>add</i>}
              onClick={handleOpenPlanificarContratacion}
            >
              Añadir clases
            </Button>
          </div>
        )}
        <div className={css.content}>
          <PageLoading isLoading={cargandoAsistencias}>
            {asistencias.length > 0 && (
              <InformationTable
                size='small'
                details={[
                  {
                    title: (
                      <Checkbox
                        checked={
                          asistenciasSeleccionadas.length === asistencias.length
                        }
                        onChange={handleAllAsistenciasSeleccionadas}
                      />
                    ),
                    key: 'checkbox',
                    sortDisabled: true
                  },
                  { title: 'Fecha', key: 'fecha', sortFunction: dateSort },
                  { title: 'Hora', key: 'hora' },
                  { title: 'Centro', key: 'centro' },
                  { title: 'Asignatura', key: 'asignatura' },
                  { title: 'Profesor', key: 'profesor' },
                  {
                    title: 'Asistencia',
                    key: 'asistencia',
                    align: 'center',
                    sortDisabled: true
                  },
                  { title: 'Clase', key: 'tipo' },
                  {
                    title: 'Observaciones',
                    key: 'observaciones',
                    sortDisabled: true,
                    cell: css.observaciones
                  },
                  {
                    title: 'Opciones',
                    key: 'opciones',
                    align: 'center',
                    sortDisabled: true
                  }
                ]}
                data={asistencias.map(asistencia => ({
                  checkbox: (
                    <Checkbox
                      checked={asistenciasSeleccionadas.includes(asistencia.id)}
                      onChange={() =>
                        handleAsistenciaSeleccionada(asistencia.id)
                      }
                    />
                  ),
                  tipo: asistencia.clase,
                  centro: asistencia.centro,
                  profesor: asistencia.profesor || <SinProfesor />,
                  asignatura: asistencia.asignatura,
                  fecha: asistencia.fecha,
                  hora: asistencia.hora,
                  asistencia: (
                    <EstadoAsistencia asistencia={asistencia} centro='' />
                  ),
                  observaciones: asistencia.observaciones,
                  opciones: (
                    <IconButton
                      size='small'
                      color='secondary'
                      onClick={() => setAsistenciaEdit(asistencia)}
                    >
                      <i className='material-icons'>edit</i>
                    </IconButton>
                  )
                }))}
              />
            )}
          </PageLoading>
        </div>
      </div>
      <ModalPlanificarClasesTarifa
        open={openPlanificarTarifa}
        tarifa={contratacion}
        alumnoId={alumnoId}
        disabled={isAsistenciaPlanning}
        onClose={() => setOpenPlanificarTarifa(false)}
        onSubmit={handlePlanificarClases}
      />
      <ModalPlanificarClasesPack
        open={openPlanificarPack}
        alumnoId={alumnoId}
        pack={contratacion}
        disabled={isAsistenciaPlanning}
        onClose={() => setOpenPlanificarPack(false)}
        onSubmit={handlePlanificarClases}
      />
      <ModalEditarAsistencia
        open={!!asistenciaEdit}
        asistencia={asistenciaEdit}
        onClose={() => setAsistenciaEdit(null)}
        onSubmit={handleUpdateAsistencia}
        isLoading={isAsistenciaUpdating}
      />
      <ModalEditarProfesorAsistencias
        open={bulkEditProfesor}
        onClose={() => setBulkEditProfesor(false)}
        onSubmit={handleUpdateProfesor}
      />
      <ModalEditarCentroAsistencias
        open={bulkEditCentro}
        onClose={() => setBulkEditCentro(false)}
        onSubmit={handleUpdateAcademia}
      />
      <ModalEditarAsignaturaAsistencias
        open={bulkEditAsignatura}
        onClose={() => setBulkEditAsignatura(false)}
        onSubmit={handleUpdateAsignatura}
      />
    </PageLoading>
  )
}

export default GestionarAsistencias
